import { TradeItem } from '@/types';

/**
 * Domain math for a single trade. Pure functions with no React/store deps —
 * trivially testable and reusable (MetricsBar aggregation, FloatingActionBar
 * selection totals, future drill-down views).
 *
 * Sign convention across all exposure calculations: `+` = in our favour
 * (due to us — CP posts / pays us), `−` = against us (we owe / pay CP). The
 * SINGLE source of truth for the sign is the customer's worked example
 * "Repricing - MtM.xlsx" Scenario 2 (Stock Loan AAPL 150→200 = +5,000; Stock
 * Borrow NUGT 252→300 = −48,000) and §5 of docs/planning/LR-104/customer-decisions.md.
 * Same direction as the backend MtM `net` (positions/views.py bulk_mark:
 * `direction = +1 lend, −1 borrow`) and the MtM page's `computeMtmExposure`
 * (mtm-calculations.ts). Net: a rise in the security price is `+` for the
 * securities lender (lend/repo) and `−` for the securities borrower (borrow/reverse).
 *
 * IMPORTANT: `contract_value` on the backend is `quantity × margin_price`
 * where `margin_price = gross_price × margin_decimal` (multiplicative,
 * `margin_decimal = margin_bps / 10000`; 100% margin → margin_decimal = 1).
 * That means `contract_value` already bakes in the margin overlay — it equals
 * the posted collateral amount for Stock L/B or the contract cash for Repo.
 * Do NOT multiply it again by the margin factor — that would double-count.
 */

/** Last MTM price, else gross price. Both nullable → 0 to stay NaN-safe. */
export const getEffectivePrice = (t: TradeItem): number =>
  t.last_marked_price ?? t.gross_price ?? 0;

/** Current market value of the shares on the trade, in trade currency. */
export const getMarketValue = (t: TradeItem): number => {
  const v = getEffectivePrice(t) * (t.quantity ?? 0);
  return Number.isFinite(v) ? v : 0;
};

/**
 * Signed exposure per trade. See convention above (`+` = due to us).
 *
 * ALL FOUR types compare a MARGINED market value against the booked cash:
 *   lend / repo:           mv × md − cv   (shares up → CP under-collateralised, owes us → +)
 *   borrow / reverse_repo: cv − mv × md   (shares up → we owe more → due to CP → −)
 * where `mv` = qty × effective price (last mark, else gross), `cv` = contract_value
 * (= qty × margin_price, already margined), and `md` = margin / 100.
 *
 * Per the customer (§5 of docs/planning/LR-104/customer-decisions.md, confirmed Jun 2026)
 * BOTH the market value and the booked cash carry the margin — "repo margins exactly like
 * Stock L/B". So a freshly-booked margined trade nets to 0 at inception (mv × md == cv when
 * current == booking); only a price move is called, the buffer is never shown as day-1
 * exposure. Mirrors the MtM page's computeMtmExposure (margined new value) and the
 * single-trade AdequacyCard (closing_cash − contract_value). At 100% margin md = 1, so it
 * reduces to mv − cv and the Scenario-2 worked examples (margin 100%) are unchanged.
 * (Missing/zero `margin` → md = 1.)
 *
 * [LR-457] fixed the lend/borrow sign; [LR-414] aligned their margin treatment with the repo
 * branch + computeMtmExposure (previously lend/borrow used the un-margined `mv`, which
 * understated the day-1 buffer and was wrong at margin ≠ 100%).
 */
export const getTradeExposure = (t: TradeItem): number => {
  const mv = getMarketValue(t);
  const cv = t.contract_value ?? 0;
  const md = t.margin && t.margin > 0 ? t.margin / 100 : 1;
  const mvMargin = mv * md;
  switch (t.trade_type) {
    case 'lend':
    case 'repo':
      return mvMargin - cv;
    case 'borrow':
    case 'reverse_repo':
      return cv - mvMargin;
    default:
      return 0;
  }
};

/**
 * Contribution of this trade to the MetricsBar "Total Value" bucket totals.
 * (The per-row "Total Value" column, LR-306, was retired in LR-522 — its
 * concept is COB Gross Value; this helper now feeds only the MetricsBar.)
 *
 * Stock L/B: AC-2 strict fallback chain — `qty × latest_mark_price` when
 * MTM was booked, `qty × gross_price` otherwise. Uses `latest_mark_price`
 * (raw, null when never marked), NOT `last_marked_price` (which the
 * backend masks with `current_market_price` for exposure / risk math).
 * Keeping the two distinct lets the "Last Marked Gross Price" column honestly
 * render `—` for an unmarked trade while this total still uses the booking
 * value — both reading from the same field family.
 *
 * Repo / reverse_repo: contract value (frozen at trade inception); the
 * mark doesn't move the headline number for repo per spec.
 */
export const getTradeTotalValue = (t: TradeItem): number => {
  switch (t.trade_type) {
    case 'lend':
    case 'borrow': {
      const price = t.latest_mark_price ?? t.gross_price ?? 0;
      const v = price * (t.quantity ?? 0);
      return Number.isFinite(v) ? v : 0;
    }
    case 'repo':
    case 'reverse_repo':
      return t.contract_value ?? 0;
    default:
      return 0;
  }
};

/**
 * Relative threshold (fraction of booking value) above which a repo trade is
 * flagged as needing a Mark-to-Market call. Starts as a fixed constant per
 * LR-307; promoting it into a user-facing setting is an explicit follow-up.
 */
export const MTM_REQUIRED_THRESHOLD_PCT = 0.05;

/**
 * `true` when the gap between current market value (`qty × current_market_price`)
 * and the booking market value (`qty × gross_price`) exceeds `thresholdPct` (5%).
 *
 * Uses gross prices, so the threshold is the pure price drift `|cmp − gross| / gross`.
 * This is margin-invariant: the backend call scales both sides by the margin factor, but
 * that factor cancels in the ratio (exposure / trade_cash = (cmp − gross) / gross). So a
 * margined repo is flagged on the same drift as a 100% one — not spuriously from the buffer.
 *
 * Only repo / reverse_repo are flagged — Stock L/B carries no booking-price
 * lock, so the concept does not apply. Missing/zero gross price or a missing
 * current market price both return `false`: the indicator must be an actionable
 * signal, not a placeholder for "data not loaded".
 *
 * Pure hint for the trader. The decision to actually book an MTM call stays
 * with the human; this only surfaces the candidate rows. LR-307 AC-2.
 */
export const isMtmRequired = (
  t: TradeItem,
  thresholdPct: number = MTM_REQUIRED_THRESHOLD_PCT,
): boolean => {
  if (t.trade_type !== 'repo' && t.trade_type !== 'reverse_repo') return false;
  const qty = t.quantity ?? 0;
  const gross = t.gross_price ?? 0;
  const booking = gross * qty;
  if (!Number.isFinite(booking) || booking === 0) return false;
  const cmp = t.current_market_price;
  if (cmp == null || !Number.isFinite(cmp)) return false;
  const market = cmp * qty;
  if (!Number.isFinite(market)) return false;
  return Math.abs(market - booking) / Math.abs(booking) > thresholdPct;
};
